import { useState } from "react";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { Button } from "@/shared/components/shadcn/Button";
import { FullScreenDialog, type DialogDismissReason } from "@/shared/components/ui/FullScreenDialog";
import { formatMoney } from "@/shared/utils/formatters";
import { useServices } from "../hooks/serviceQueries";
import type { Service } from "../types/serviceTypes";
import { ServicesList } from "./ServicesList";

interface ServicePickerProps {
  open: boolean;
  selectedServices?: Service[];
  onClose: (reason?: DialogDismissReason) => void;
  onSelected: (services: Service[]) => void;
}

export function ServicePicker({ open, ...props }: ServicePickerProps) {
  return open ? <ServicePickerDraft {...props} /> : null;
}

function ServicePickerDraft({ selectedServices = [], onClose, onSelected }: Omit<ServicePickerProps, "open">) {
  const { business } = useCurrentBusiness();
  const catalogue = useServices();
  const [originalSelections] = useState(selectedServices);
  const [draft, setDraft] = useState(selectedServices);
  const total = draft.reduce((sum, service) => sum + (service.basePrice ?? 0), 0);
  const hasUnavailableAddition = draft.some((service) =>
    !originalSelections.some((original) => original.id === service.id) &&
    !catalogue.data?.some((current) => current.id === service.id && current.isActive),
  );
  const apply = () => {
    if (catalogue.isPending || catalogue.error || hasUnavailableAddition) return;
    // Reuse original line-item objects: catalogue updates must not replace booked values.
    const unchanged = draft.length === originalSelections.length &&
      draft.every((service) => originalSelections.some((original) => original.id === service.id));
    onSelected(unchanged ? originalSelections : draft);
    onClose();
  };

  return (
    <FullScreenDialog
      title="Select services"
      onClose={onClose}
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <div role="status" aria-live="polite" className="min-w-0 text-sm">
            <strong className="block">{draft.length} selected</strong>
            <span className="text-muted-foreground">{business ? formatMoney(total, business.currency) : "Currency unavailable"}</span>
          </div>
          <Button size="touch" onClick={apply} disabled={catalogue.isPending || !!catalogue.error || hasUnavailableAddition}>
            Use {draft.length} {draft.length === 1 ? "service" : "services"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 px-4 py-4 sm:px-[30px]">
        {hasUnavailableAddition && <p role="alert" className="text-sm text-destructive">A selected service is no longer available. Close and reopen the picker to refresh your selection.</p>}
        <ServicesList
          mode="picker"
          existingSelections={originalSelections}
          selectedServices={draft}
          onServiceClick={(service) => setDraft((current) => current.some((item) => item.id === service.id)
            ? current.filter((item) => item.id !== service.id)
            : [...current, service])}
        />
      </div>
    </FullScreenDialog>
  );
}
